import type { Testimonial } from '@musicville/shared';
import { Avatar, Section, Skeleton } from '@/components/ui/primitives';
import { useI18n } from '@/lib/i18n';
import { useTestimonials } from '@/lib/queries';

export function StudentStories(): React.JSX.Element {
  const { t } = useI18n();
  const { data, isPending } = useTestimonials();

  const [lead, ...supporting] = data ?? [];

  return (
    <Section subtle>
      <p className="text-overline font-semibold uppercase text-accent">{t('stories.overline')}</p>

      {isPending ? (
        <div className="mt-12 grid gap-16 lg:grid-cols-[700px_1fr]">
          <Skeleton className="h-60 rounded-lg" />
          <Skeleton className="h-60 rounded-lg" />
        </div>
      ) : (
        lead && (
          <div className="mt-12 flex flex-col gap-16 lg:flex-row">
            <figure className="flex flex-col gap-7 lg:w-[700px] lg:shrink-0">
              <blockquote className="font-display text-h2 font-normal leading-[1.16] text-primary">
                “{lead.quote}”
              </blockquote>
              <figcaption className="flex items-center gap-3">
                <Avatar name={lead.authorName} size="md" />
                <span className="flex flex-col">
                  <span className="text-body-sm font-medium text-primary">{lead.authorName}</span>
                  <span className="text-caption text-tertiary">{lead.authorContext}</span>
                </span>
              </figcaption>
            </figure>

            <div className="flex flex-1 flex-col gap-8">
              {supporting.slice(0, 2).map((story) => (
                <SupportingQuote key={story.id} story={story} />
              ))}
            </div>
          </div>
        )
      )}
    </Section>
  );
}

function SupportingQuote({ story }: { story: Testimonial }): React.JSX.Element {
  return (
    <figure className="flex flex-col gap-3.5 border-l-2 border-border-default pl-6">
      <blockquote className="font-display text-h3 font-normal leading-[1.4] text-primary">
        “{story.quote}”
      </blockquote>
      <figcaption className="flex flex-col">
        <span className="text-body-sm font-medium text-primary">{story.authorName}</span>
        <span className="text-caption text-tertiary">{story.authorContext}</span>
      </figcaption>
    </figure>
  );
}
